<template>
  <div>
    <div class="form-group--inline justify-start">
      <label for="selectSortby" class="form-label">
        {{ getBaseProperty('sortby') }}
      </label>
      <div class="select select--clear w-auto inline-block">
        <select
          id="selectSortby"
          v-model="sortKey"
          class="form-control w-auto"
          @change="$emit('update-sort-type', sortKey)"
        >
          <option
            v-for="(column, colIndex) in sortOptions"
            :key="colIndex"
            :value="column.param"
            :qaid="`sort-list.option-${column.param}`"
          >
            {{ column.name }}
          </option>
        </select>
        <div class="focus-indicator"></div>
      </div>
    </div>

    <span v-if="minQtyErrorPresent">{{ bp('skuGridErrorMessage') }}</span>

    <table class="b2b-grid b2b-grid--collapse is-compact" qaid="saved-list-sku-grid">
      <thead class="b2b-grid__header">
        <tr>
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.checkbox">
            <label class="checkbox">
              <input
                id="saved-lists-select-all"
                type="checkbox"
                :checked="allSelected"
                :disabled="disableInteractions"
                @change="selectAll"
              />
              <span class="control-indicator"></span>
            </label>
          </th>
          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.note"></th>
          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.sku">
            {{ getBaseProperty('sku') }}
          </th>
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.description">
            {{ getBaseProperty('description') }}
          </th>
          <th class="b2b-grid__cell b2b-grid__head uppercase lg:hidden" qaid="sku-grid.upc">
            {{ getBaseProperty('upc') }}
          </th>
          <th class="b2b-grid__cell b2b-grid__head whitespace-nowrap" qaid="sku-grid.date-added">
            {{ getBaseProperty('dateAdded') }}
          </th>
          <!-- Variable warehouse headers (1 to 5) -->
          <template v-if="!isDropShipFlagEnabled">
            <th
              v-for="(warehouse, warehouseKey) in warehouseList"
              :key="warehouseKey"
              class="b2b-grid__cell b2b-grid__head uppercase"
              data-type="warehouse"
              qaid="sku-grid.warehouse"
            >
              {{ warehouse }}
            </th>
            <th
              class="b2b-grid__cell b2b-grid__head whitespace-nowrap"
              data-type="stock-watch"
              qaid="sku-grid.stock-watch"
            >
              {{ getBaseProperty('stockWatch') }}
            </th>
          </template>
          <th v-if="!consumerFriendlyMode" class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.order-price">
            {{ getBaseProperty('price') }}
          </th>
          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.qty">
            {{ getBaseProperty('qty') }}
          </th>
          <th v-if="!consumerFriendlyMode" class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.subtotal">
            {{ getBaseProperty('subtotal') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- Each Row -->
        <template v-for="(row, rowIndex) in paginatedData">
          <saved-list-grid-item
            :key="`${row.sku}-${rowIndex}`"
            :row="row"
            :row-index="rowIndex"
            :base-properties="baseProperties"
            :disable-interactions="disableInteractions"
            :entries-name="entriesName"
            :min-qty-move-error="minQtyMoveFailedList && minQtyMoveFailedList[row.sku] ? true : false"
            :min-qty-move-error-msg="minQtyMoveFailedList[row.sku] ? minQtyMoveFailedList[row.sku] : null"
            @refresh="refresh"
            @min-qty-error-status-emitter="minQtyErrorPresentUpdater"
            @send-list-update="sendListUpdate"
          />
        </template>
      </tbody>
    </table>

    <div v-show="selectedList.id">
      <div class="flex flex-wrap mb-1 pb-5">
        <div class="w-full lg:w-auto lg:flex-shrink self-center text-center pb-5">
          <pagination
            v-if="pageCount > 1"
            ref="pagination"
            :page-number="pageNumber"
            :page-count="pageCount"
            @page-change="pageChange"
            @prev-page="prevPage"
            @next-page="nextPage"
          ></pagination>
        </div>
        <slot name="summary"></slot>
        <div class="w-full lg:w-auto lg:flex-grow">
          <div class="buttons buttons--right-for-md">
            <trek-button
              v-if="isB2BPurchasable"
              primary
              icon="shopping_cart"
              qaid="saved-lists.button-move-to-cart"
              :disabled="$store.state.cartToggle === false || disableActions"
              @click="$emit('grid-move-to-cart')"
              ><span v-html="bp('moveToCart')"></span>
            </trek-button>
            <trek-button
              v-if="!disableInteractions"
              secondary
              qaid="saved-lists.button-remove"
              :disabled="disableActions"
              @click="$emit('grid-remove')"
              ><span v-html="bp('remove')"></span
            ></trek-button>
            <trek-button
              v-if="!selectedList.readOnly && !disableInteractions"
              tertiary
              qaid="saved-lists.button-delete"
              :disabled="disableInteractions"
              @click="$emit('grid-delete')"
              ><span v-html="bp('delete')"></span
            ></trek-button>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :center="true"
      :z-index="16000011"
      :show-close="false"
      :visible.sync="dialogVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :title="bp('confirmDeleteListItem')"
      qaid="saved-lists.delete.row.modal"
      :close-dialog-tooltip="bp('closeDialogToolTip')"
      @close="closeDialog"
    >
      <template slot="footer">
        <div class="buttons buttons--center">
          <trek-button alert qaid="saved-lists.delete.row.yes" @click="confirmDelete"
            ><span v-html="bp('yesDelete')"></span
          ></trek-button>
          <trek-button secondary qaid="saved-lists.delete.row.no" @click="closeDialog"
            ><span v-html="bp('save')"></span
          ></trek-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import find from 'lodash/find';
import get from 'lodash/get';
import ElDialog from '../Dialog.vue';
import pagination from '../../mixins/pagination';
import Pagination from '@/components/Pagination';
import SavedListGridItem from '@/components/saved-lists/SavedListGridItem';
import {mapActions, mapState, mapGetters} from 'vuex';
import TrekButton from '@/components/TrekButton';

export default {
  components: {ElDialog, TrekButton, Pagination, SavedListGridItem},

  mixins: [pagination],

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    disableInteractions: {
      type: Boolean,
      default: false,
    },
    disableActions: {
      type: Boolean,
      default: false,
    },
    entriesName: {
      type: String,
      default: '',
    },
    list: {
      type: Array,
      default: () => [],
    },
    size: {
      type: Number,
      required: false,
      default: 15,
    },
    minQtyMoveFailedList: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      dialogVisible: false,
      sku: null,
      sortKey: 'savedlistdateadded',
      sortOptions: [
        {name: this.getBaseProperty('dateAdded'), param: 'savedlistdateadded'},
        {name: this.getBaseProperty('description'), param: 'description'},
        {name: this.getBaseProperty('qty'), param: 'quantity'},
        {name: this.getBaseProperty('sku'), param: 'code'},
      ],
      minQtyUpdateErrorMsg: [],
      minQtyErrorPresent: false,
    };
  },

  computed: {
    listData() {
      return this.list;
    },

    allSelected() {
      return this.selectedList[this.entriesName].every((entry) => {
        return entry.checked;
      });
    },

    warehouseList() {
      return get(this.list[0], 'warehouses', []).map((x) => x.id);
    },

    ...mapState('savedLists', ['selectedList']),
    ...mapState('backend', ['isB2BPurchasable', 'isDropShipFlagEnabled']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  watch: {
    listData() {
      this.pageNumber = 0;
    },
  },

  created() {
    this.$on('row:delete', this.openDialog);
  },

  methods: {
    ...mapActions('savedLists', ['removeSelectedEntries', 'fetchList', 'updateQty']),

    refresh() {
      this.fetchList(this.selectedList.id);
    },

    getBaseProperty(name) {
      let baseProperty = find(this.baseProperties, (baseProperty) => baseProperty.name === name);
      return baseProperty ? baseProperty.label : '';
    },

    minQtyErrorPresentUpdater(status) {
      this.minQtyErrorPresent = status;
    },

    minQtyUpdateErrorUpdater(msg) {
      this.minQtyUpdateErrorMsg = msg;
    },

    selectAll(event) {
      if (this.selectedList[this.entriesName]) {
        this.selectedList[this.entriesName] = this.selectedList[this.entriesName].map((entry) => {
          entry.checked = event.target.checked;

          return entry;
        });
      }
    },

    openDialog(sku) {
      this.sku = sku;
      this.dialogVisible = true;
    },

    closeDialog() {
      this.$emit('update-sort-type', this.sortKey); // Refresh list after user cancels initial Qty change
      this.sku = null;
      this.dialogVisible = false;
    },

    sendListUpdate(listData) {
      this.updateQty({
        sku: listData.sku,
        qty: listData.qty,
        checked: listData.checked,
        lineNote: listData.lineNote.length > 0 ? listData.lineNote : null,
      }).then((data) => {
        if (get(data, 'data.failedSKUs')) {
          this.minQtyItemUpdateError = true;
          this.minQtyUpdateErrorMessage = (get(data, 'data.failedSKUs') || []).map(
            (item) => item.message.basePropertyValue
          )[0];
        } else {
          this.minQtyItemUpdateError = false;
          this.minQtyUpdateErrorMessage = null;
        }
        this.refresh();
      });
    },

    confirmDelete() {
      this.removeSelectedEntries([{sku: this.sku, qty: 0}]).then(() => {
        this.dialogVisible = false;
        this.sku = null;
      });
    },
  },
};
</script>
